type Wav = {
  format: Uint8Array;
  data: Uint8Array;
};

const readTag = (buffer: ArrayBuffer, offset: number) => String.fromCharCode(...new Uint8Array(buffer, offset, 4));

const readWav = (buffer: ArrayBuffer): Wav => {

  if (readTag(buffer, 0) !== 'RIFF' || readTag(buffer, 8) !== 'WAVE') throw new Error('Not a WAV file');

  const view = new DataView(buffer);
  let offset = 12;
  let format: Uint8Array | null = null;

  while (offset + 8 <= buffer.byteLength) {

    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;

    if (readTag(buffer, offset) === 'fmt ') format = new Uint8Array(buffer, body, size);

    if (readTag(buffer, offset) === 'data') {

      if (!format) throw new Error('WAV data before format');

      // A writer that never finalized the header leaves a size that does not fit the file.
      return { format, data: new Uint8Array(buffer, body, Math.min(size, buffer.byteLength - body)) };

    }

    offset = body + size + (size % 2);

  }

  throw new Error('WAV without data');

};

const sameBytes = (a: Uint8Array, b: Uint8Array) => a.length === b.length && a.every((byte, index) => byte === b[index]);

const writeTag = (view: DataView, offset: number, tag: string) => {

  [...tag].forEach((char, index) => view.setUint8(offset + index, char.charCodeAt(0)));

};

/**
 * Joins WAV files that were cut from one continuous recording into a single WAV, sample for sample, so the
 * player has no gap at the joins. Every file must have the same format.
 */
export const joinWavs = (buffers: ArrayBuffer[]): Blob => {

  const wavs = buffers.map(readWav);
  const { format } = wavs[0];

  if (!wavs.every((wav) => sameBytes(wav.format, format))) throw new Error('WAV chunks differ in format');

  const dataLength = wavs.reduce((total, wav) => total + wav.data.length, 0);
  const header = new DataView(new ArrayBuffer(20 + format.length + 8));

  writeTag(header, 0, 'RIFF');
  header.setUint32(4, 4 + 8 + format.length + 8 + dataLength, true);
  writeTag(header, 8, 'WAVE');
  writeTag(header, 12, 'fmt ');
  header.setUint32(16, format.length, true);
  new Uint8Array(header.buffer).set(format, 20);
  writeTag(header, 20 + format.length, 'data');
  header.setUint32(24 + format.length, dataLength, true);

  return new Blob([header, ...wavs.map((wav) => wav.data)], { type: 'audio/wav' });

};
